/**
 * Customer Implementation Centre — Stage 17.
 * Compact list + detail: workstream readiness, risks/issues, decisions, actions.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Plus, RefreshCw, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";

export default function ImplementationCentre() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [masters, setMasters] = useState([]);

  const load = () => api.get("/implementation").then(({ data }) => setItems(data.items || []));
  useEffect(() => {
    load();
    api.get("/master-companies").then(({ data }) => setMasters(data.items || []));
  }, []);

  return (
    <div className="max-w-6xl space-y-6" data-testid="impl-centre">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Customer success</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Implementation Centre</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Onboard every new customer inside ONE — not in spreadsheets. Track workstreams, decisions, risks and customer actions
          from Planning through Hypercare.
        </p>
      </div>

      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1" data-testid="impl-new">
          <Plus className="h-4 w-4" /> New project
        </button>
      </div>

      {showForm && <ProjectForm masters={masters} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border border-slate-200 rounded-md divide-y">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No implementation projects yet.</div>
          ) : items.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} data-testid={`impl-row-${p.project_number}`}
              className={"w-full text-left p-4 hover:bg-slate-50 " + (selected?.id === p.id ? "bg-slate-50" : "")}>
              <div className="text-sm font-semibold text-slate-900 font-mono">{p.project_number}</div>
              <div className="text-xs text-slate-600 mt-0.5">{p.name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 bg-slate-50">{p.state?.replace("_", " ")}</span>
                <span className="text-[10px] tabular-nums text-slate-500">{p.readiness_pct}%</span>
              </div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2">
          {selected ? <ProjectDetail projectId={selected.id} onChanged={load} /> : (
            <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-md p-8">Select a project on the left.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectForm({ masters, onCancel, onCreated }) {
  const [f, setF] = useState({ master_company_id: masters[0]?.id || "", name: "SUNTRONIX ONE Implementation", target_go_live: "", countries: "IN" });
  const save = async () => {
    try {
      await api.post("/implementation", { ...f, countries: f.countries.split(",").map((c) => c.trim()).filter(Boolean) });
      toast.success("Project created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="impl-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Customer</div>
          <select className="input" value={f.master_company_id} onChange={(e) => setF({ ...f, master_company_id: e.target.value })}>
            <option value="">—</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Project name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Target go-live</div>
          <input type="date" className="input" value={f.target_go_live} onChange={(e) => setF({ ...f, target_go_live: e.target.value })} />
        </label>
        <label className="md:col-span-3">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Countries (comma-separated ISO codes)</div>
          <input className="input" value={f.countries} onChange={(e) => setF({ ...f, countries: e.target.value })} placeholder="IN, SG, UK" />
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
        <button onClick={save} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md" data-testid="impl-save">Create</button>
      </div>
    </div>
  );
}

function ProjectDetail({ projectId, onChanged }) {
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`/implementation/${projectId}`).then(({ data }) => setP(data)), [projectId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load, projectId]);

  const updateWs = async (ws_id, readiness) => {
    await api.post(`/implementation/${projectId}/workstream`, { workstream_id: ws_id, readiness });
    toast.success("Updated");
    load(); onChanged();
  };

  if (!p) return <div className="text-sm text-slate-500 p-4">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-md bg-slate-900 text-white grid place-items-center">
            <Rocket className="h-5 w-5" strokeWidth={1.4} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold text-slate-900">{p.name}</div>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">{p.project_number} · {p.state?.replace("_", " ")} · target {p.target_go_live || "TBD"}</div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-heading font-semibold text-slate-900 tabular-nums">{p.readiness_pct}%</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Readiness</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">Workstreams</div>
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {p.workstreams.map((w) => (
            <div key={w.id} className="border border-slate-100 rounded-md p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-800">{w.name}</div>
                <div className="text-xs text-slate-500 tabular-nums">{w.readiness}%</div>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className={"h-full " + (w.readiness >= 80 ? "bg-emerald-500" : w.readiness >= 40 ? "bg-amber-500" : "bg-slate-400")} style={{ width: `${w.readiness}%` }} />
              </div>
              <div className="mt-2 flex gap-1">
                {[0, 25, 50, 75, 100].map((v) => (
                  <button key={v} onClick={() => updateWs(w.id, v)} className="text-[10px] font-mono text-slate-500 hover:text-slate-900 border border-slate-100 rounded px-1 py-0.5">
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ListPanel title="Risks & issues" items={p.risks} icon={AlertTriangle} empty="No risks logged." labelKey="description" tagKey="severity" />
        <ListPanel title="Decisions" items={p.decisions} icon={CheckCircle2} empty="No decisions logged." labelKey="decision" tagKey="impact" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">Open customer actions</div>
        <div className="divide-y divide-slate-100">
          {p.actions.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">No pending actions.</div>
          ) : p.actions.map((a) => (
            <div key={a.id} className="p-3 flex items-center gap-3">
              <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (a.owner === "customer" ? "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" : "text-sky-700 bg-sky-50 border-sky-200")}>{a.owner}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{a.title}</div>
                {a.due_date && <div className="text-xs text-slate-500">Due {a.due_date}</div>}
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListPanel({ title, items, icon: Icon, empty, labelKey, tagKey }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800 flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" /> {title}
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">{empty}</div>
        ) : items.map((it) => (
          <div key={it.id} className="p-3">
            <div className="flex items-center gap-2 mb-0.5">
              {it[tagKey] && <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 bg-slate-50">{it[tagKey]}</span>}
            </div>
            <div className="text-sm text-slate-800">{it[labelKey]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
